// Real auth store — replaces the step-6 header-picker stub.
//
// Access tokens live in a Zustand store scoped to page lifetime (never
// localStorage, per ADR 0020 §6). The refresh cookie lives in the
// browser's cookie jar, inaccessible to JS.
//
// Filename is preserved from step 6 so the import paths don't churn;
// the "stub" name is a historical artefact.

import { create } from "zustand";

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  display_name: string;
  is_admin: boolean;
  totp_enabled: boolean;
  webdav_mount_url?: string;
  /** Chosen UI language; ``null`` follows the browser. */
  locale?: string | null;
  /** Clicking an Office document's name: open the editor or download. */
  office_open?: "editor" | "download";
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  /**
   * True once the app-start session restore (refresh cookie →
   * access token) has settled, successfully or not. Gates the
   * "loading" vs "log in" decision in AuthGate.
   */
  sessionChecked: boolean;
  setSession: (accessToken: string, user: AuthUser) => void;
  setAccessToken: (token: string | null) => void;
  markSessionChecked: () => void;
  clear: () => void;
}

export const useAuth = create<AuthState>()((set) => ({
  accessToken: null,
  user: null,
  sessionChecked: false,
  setSession: (accessToken, user) => set({ accessToken, user }),
  setAccessToken: (accessToken) => set({ accessToken }),
  markSessionChecked: () => set({ sessionChecked: true }),
  clear: () => set({ accessToken: null, user: null }),
}));

/** Read the current access token synchronously — used by the fetch wrapper. */
export function getAccessToken(): string | null {
  return useAuth.getState().accessToken;
}
